import React, {
    FC,
    useState,
    useEffect,
    MouseEvent,
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    useRef,
    MutableRefObject
} from 'react';
import styled from '@emotion/styled';
import {
    InputAutoSuggestWithLabelProps,
    Item
} from './InputAutoSuggestWithLabel.types';
import {
    StyledWrapper,
    StyledInput,
    StyledIconButton,
    StyledSuggestionButton,
    StyledList,
    StyledListItem,
    StyledValue,
    LocationPinIcon,
    StyledButtonText,
    StyledSearchLabel
} from './InputAutoSuggestWithLabel.styles';

import { onDocumentClick } from '@blueprint/effects';
import { HighlightedText } from './HighlightedText';
import { ClearOutlineMedium } from '@blueprint/icons';

const CLEAR_ICON_VALUE = 'Clear';

const InputAutoSuggestWithLabelComponent: FC<InputAutoSuggestWithLabelProps> = (
    props
) => {
    const [suggestions, setSuggestions] = useState<Item[]>([]);
    const [searchText, setSearchText] = useState(props.value || '');
    const [suggestionIdx, setSuggestionIdx] = useState(-1);
    const [isActive, setIsActive] = useState(false);
    const {
        label = 'Search area',
        hasExtendedHeight,
        hasBorderRight = true,
        showError = false,
        hasDarkPurpleBorder = false,
        onlyDisplayRemoveIconWhenActive = true
    } = props;

    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const rootNode = useRef() as MutableRefObject<HTMLDivElement>;
    const inputNode = useRef<HTMLInputElement>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
        onDocumentClick((evt: globalThis.MouseEvent) => {
            if (
                rootNode.current &&
                rootNode.current.contains(evt.target as Node)
            ) {
                return;
            }

            setSuggestions([]);
            setSuggestionIdx(-1);
        }),
        []
    );

    useEffect(() => {
        setSearchText(props.value || '');
    }, [props.value]);

    const onTextChange = async (evt: ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value }
        } = evt;

        setSearchText(value);
        if (props.inputTextChange) {
            props.inputTextChange(value);
        }

        if (props.validInputRegex && !props.validInputRegex.test(value)) {
            return setSuggestions([]);
        }

        const data = await props.getData(value);
        setSuggestions(data || []);
    };

    const onClearButtonClick = () => {
        setSearchText('');
        setSuggestions([]);
        setSuggestionIdx(-1);
        inputNode.current?.focus();
        setIsActive(true);
    };

    const onSuggestionClick = (evt: MouseEvent<HTMLButtonElement>) => {
        const {
            currentTarget: {
                value,
                dataset: { id = '' }
            }
        } = evt;

        setSuggestions([]);
        setSearchText(value);

        if (props.suggestionSelectedAction) {
            props.suggestionSelectedAction({ id, value });
        }
    };

    const onKeyDown = (evt: KeyboardEvent) => {
        const { keyCode } = evt;
        const { length } = suggestions;
        let idx = -1;
        const keyUp = 38;
        const keyDown = 40;
        const keyEsc = 27;
        const keyEnter = 13;

        if (suggestionIdx === -1 && keyCode === keyEnter) {
            if (props.inputOnKeyDown) {
                props.inputOnKeyDown(evt);
            }
            return;
        }

        if (keyCode === keyUp || keyCode === keyDown) {
            evt.preventDefault();
        }

        if (keyCode === keyUp && suggestionIdx > 0) {
            idx = suggestionIdx - 1;
        } else if (keyCode === keyUp && suggestionIdx === 0) {
            idx = length - 1;
        } else if (keyCode === keyDown && suggestionIdx < length - 1) {
            idx = suggestionIdx + 1;
        } else if (keyCode === keyDown && suggestionIdx === length - 1) {
            idx = 0;
        } else if (keyCode === keyEsc && suggestions != []) {
            setSearchText('');
            setSuggestions([]);
            setSuggestionIdx(-1);
        } else if (keyCode === keyEnter) {
            if (suggestions.length) {
                evt.stopPropagation();
                setSuggestions([]);
                const currentEl = itemRefs.current[
                    suggestionIdx === -1 ? 0 : suggestionIdx
                ] as HTMLButtonElement;
                const currentValue = currentEl.value;
                const currentId = currentEl.dataset.id as string;

                setSearchText(currentValue);

                if (props.suggestionSelectedAction) {
                    props.suggestionSelectedAction({
                        id: currentId,
                        value: currentValue
                    });
                }
            } else {
                if (props.suggestionSelectedAction) {
                    props.suggestionSelectedAction({
                        id: '',
                        value: searchText as string
                    });
                }
            }
        }

        setSuggestionIdx(idx);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
        const { relatedTarget } = event;
        const target = relatedTarget as HTMLInputElement;

        const hasClickedClearButton =
            target && target.value === CLEAR_ICON_VALUE;

        if (hasClickedClearButton) {
            return;
        } else {
            setIsActive(false);
        }
    };

    const shouldDisplayRemoveIcon = onlyDisplayRemoveIconWhenActive
        ? searchText !== '' && isActive
        : searchText !== '';

    return (
        <StyledWrapper onKeyDown={onKeyDown} ref={rootNode}>
            <StyledInput
                {...props}
                type="text"
                onChange={onTextChange}
                value={searchText}
                hasSuggestions={suggestions.length > 0}
                data-testid="autosuggest-input"
                aria-label="Location"
                ref={inputNode}
                onFocus={() => setIsActive(true)}
                onBlur={handleBlur}
                hasExtendedHeight={hasExtendedHeight}
                hasBorderRight={hasBorderRight}
                hasDarkPurpleBorder={hasDarkPurpleBorder}
            />
            <StyledSearchLabel
                hasExtendedHeight={hasExtendedHeight}
                showError={showError}
            >
                {showError ? `Please enter a valid search area` : `${label}`}
            </StyledSearchLabel>
            {shouldDisplayRemoveIcon ? (
                <StyledIconButton
                    type="button"
                    value={CLEAR_ICON_VALUE}
                    onClick={onClearButtonClick}
                    data-testid="autosuggest-clear"
                    aria-label={CLEAR_ICON_VALUE}
                    hasExtendedHeight={hasExtendedHeight}
                >
                    <ClearOutlineMedium data-testid="clear" />
                </StyledIconButton>
            ) : null}
            {suggestions.length > 0 && (
                <StyledList data-testid="autosuggest-list">
                    {suggestions.map((item, idx) => (
                        <StyledListItem key={item.id}>
                            <StyledSuggestionButton
                                data-testid={item.value}
                                isFocused={idx === suggestionIdx}
                                type="button"
                                value={item.value}
                                data-id={item.id}
                                onClick={onSuggestionClick}
                                ref={(ref) => {
                                    itemRefs.current[idx] = ref;
                                }}
                            >
                                <LocationPinIcon />
                                <StyledButtonText>
                                    {item.subValue ? (
                                        <StyledValue data-testid="styled-value">
                                            <HighlightedText
                                                text={item.value}
                                                highlight={searchText as string}
                                            />
                                        </StyledValue>
                                    ) : (
                                        <HighlightedText
                                            text={item.value}
                                            highlight={searchText as string}
                                        />
                                    )}
                                    {item.subValue}
                                </StyledButtonText>
                            </StyledSuggestionButton>
                        </StyledListItem>
                    ))}
                </StyledList>
            )}
        </StyledWrapper>
    );
};

InputAutoSuggestWithLabelComponent.displayName = 'InputAutoSuggestWithLabel';

/**
 - The InputAutoSuggestWithLabel Component allows us to type in the text box and as we type it displays the matching results in the dropdown list in alphabetical order
 - By clicking on one of the suggestions we change the content of the input box and trigger the new search for selected location
 - The InputAutoSuggestWithLabel Component must be supplied with getData, suggestionSelectedAction and placeholder props
 - We may also optionally supply a regExp  to validate the input ie: validInputProps  `/([A-Za-zÀ-ÖØ-öø-ÿ])/`

| **Team**               |       |
| -----------------------|--------------|
| **Zeroheight**         |   https://zeroheight.com/8f6f2b33a/v/latest/p/26fbf8-auto-suggest/b/47013c   |
**/

const InputAutoSuggestWithLabel = styled(InputAutoSuggestWithLabelComponent)();

export { InputAutoSuggestWithLabel };
